import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { getScrollBarWidth } from '@livekit/components-core';
import * as React from 'react';
import {TrackLoop, useVisualStableUpdate} from "@livekit/components-react";
// @ts-ignore
import {useResizeObserver} from "./useResizeObserver.ts";
// @ts-ignore
import {sortTrackReferencesCustom} from "./SortTrack.ts";

import useWindowDimensions from './../../../hooks/useWindowDimensions.js'
const MIN_HEIGHT = 130;
const MIN_WIDTH = 140;
const MIN_VISIBLE_TILES = 1;
const ASPECT_RATIO = 16 / 10;
const ASPECT_RATIO_INVERT = (1 - ASPECT_RATIO) * -1;

/** @public */

export interface CarouselLayoutProps extends React.HTMLAttributes<HTMLMediaElement> {
    tracks: TrackReferenceOrPlaceholder[];
    children: React.ReactNode;
    /** Place the tiles vertically or horizontally next to each other.
     * If undefined orientation is guessed by the dimensions of the container. */
    orientation?: 'vertical' | 'horizontal';
    enlarged? : boolean;
}

/**
 * The `CarouselLayout` component displays a list of tracks in a scroll container.
 * It will display as many tiles as possible and overflow the rest.
 * @remarks
 * To ensure visual stability when tiles are reordered due to track updates,
 * the component uses the `useVisualStableUpdate` hook.
 * @example
 * ```tsx
 * const tracks = useTracks([Track.Source.Camera]);
 * <CarouselLayout tracks={tracks}>
 *   <ParticipantTile />
 * </CarouselLayout>
 * ```
 * @public
 */
export default function CustomCarouselLayout({ tracks, orientation, enlarged, ...props }: CarouselLayoutProps) {
    const asideEl = React.useRef<HTMLDivElement>(null);
    const [prevTiles, setPrevTiles] = React.useState(0);
    const { width, height } = useSize(asideEl);

    const { width: windowWidth, height: windowHeight } = useWindowDimensions()

    const carouselOrientation = orientation
        ? orientation
        : height >= width
            ? 'vertical'
            : 'horizontal';

    const tileSpan =
        carouselOrientation === 'vertical'
            ? Math.max(width * ASPECT_RATIO_INVERT, MIN_HEIGHT)
            : Math.max(height * ASPECT_RATIO, MIN_WIDTH);
    const scrollBarWidth = getScrollBarWidth();

    const tilesThatFit =
        carouselOrientation === 'vertical'
            ? Math.max((height - scrollBarWidth) / tileSpan, MIN_VISIBLE_TILES)
            : Math.max((width - scrollBarWidth) / tileSpan, MIN_VISIBLE_TILES);

    let maxVisibleTiles = Math.round(tilesThatFit);
    if (Math.abs(tilesThatFit - prevTiles) < 0.5) {
        maxVisibleTiles = Math.round(prevTiles);
    } else if (prevTiles !== tilesThatFit) {
        setPrevTiles(tilesThatFit);
    }

    const sortedTiles = useVisualStableUpdate(tracks, maxVisibleTiles, {customSortFunction: sortTrackReferencesCustom});

    React.useLayoutEffect(() => {
        const maxVerticalTiles = enlarged ? 3 : windowHeight < 650 ? 4 : 5
        const maxHorizontalTiles = enlarged ? 3 : windowWidth < 1200 ? 4 : 6
        if (asideEl.current) {
            asideEl.current.dataset.lkOrientation = carouselOrientation;
            asideEl.current.style.setProperty('--lk-max-visible-tiles', maxVisibleTiles.toString());
            asideEl.current.style.setProperty('gap', "3px");
            asideEl.current.style.removeProperty("display")
            asideEl.current.style.removeProperty("grid-template-columns")
            if (carouselOrientation === "vertical") {
                if (sortedTiles.length > maxVerticalTiles) {
                    asideEl.current.style.setProperty("display", "grid")
                    asideEl.current.style.setProperty("grid-template-columns", "1fr 1fr")
                }
            } else if (sortedTiles.length > maxHorizontalTiles) {
                asideEl.current.style.setProperty("display", "grid")
                asideEl.current.style.setProperty("grid-template-columns", "1fr ".repeat(Math.ceil(sortedTiles.length / 2)))
            } else {
                asideEl.current.style.setProperty("display", "flex")
                asideEl.current.style.removeProperty("grid-template-columns")
            }
        }
    }, [maxVisibleTiles, carouselOrientation, windowWidth, windowHeight]);

    return (
        <aside key={carouselOrientation} className="lk-carousel" ref={asideEl} {...props}>
            <TrackLoop tracks={sortedTiles}>{props.children}</TrackLoop>
        </aside>
    );
}

const useSize = (target: React.RefObject<HTMLDivElement>) => {
    const [size, setSize] = React.useState({ width: 0, height: 0 });
    React.useLayoutEffect(() => {
        if (target.current) {
            const { width, height } = target.current.getBoundingClientRect();
            setSize({ width, height });
        }
    }, [target.current]);

    const resizeCallback = React.useCallback(
        (entry: ResizeObserverEntry) => setSize(entry.contentRect),
        [],
    );
    // Where the magic happens
    useResizeObserver(target, resizeCallback);
    return size;
};
